import axios from 'axios'
import { API_URL, getPublicUrl } from '@/config'

/**
 * Service für API-Aufrufe zur Zahlungsabwicklung
 */
export const paymentService = {
  // Basis-Konfiguration für alle Anfragen
  axiosConfig: {
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    timeout: 60000 // 60 Sekunden Timeout für alle Payment-Anfragen
  },

  // Erhalte die Erfolgs-URL für Zahlungen
  getSuccessUrl() {
    return `${getPublicUrl()}/payment/success`;
  },

  /**
   * Stripe-Checkout-Session erstellen
   * @param {number} orderId - ID der Bestellung
   * @returns {Promise<Object>} - Checkout-URL
   */
  async createStripeCheckoutSession(orderId) {
    console.log(`[paymentService] Erstelle Stripe-Session für Bestellung #${orderId}`);
    
    try {
      const response = await axios.post(`${API_URL}/payments/create-checkout-session`, {
        orderId: orderId,
        skipEmailCollection: true,
        skipAddressCollection: true,
        paymentMethod: 'stripe',
        successUrl: this.getSuccessUrl()
      }, this.axiosConfig);
      
      console.log(`[paymentService] Stripe-Session erfolgreich erstellt für Bestellung #${orderId}`);
      return response;
    } catch (error) {
      console.error(`[paymentService] Fehler beim Erstellen der Stripe-Session für Bestellung #${orderId}:`, error);
      
      // Verbesserte Fehlerdiagnose
      if (error.response) {
        console.error(`[paymentService] Server antwortet mit Status ${error.response.status}:`, error.response.data);
      } else if (error.request) {
        console.error(`[paymentService] Keine Antwort vom Server erhalten - Netzwerkfehler`);
      }
      
      // Fallback zum Legacy-Endpunkt
      console.log(`[paymentService] Versuche Legacy-Endpunkt für Bestellung #${orderId}`);
      return this.createLegacyStripeCheckoutSession(orderId);
    }
  },
  
  /**
   * Temporäre Stripe-Checkout-Session ohne Bestellung erstellen
   * @param {number} amount - Betrag der Bestellung in EUR
   * @param {Array} items - Optionale Warenkorbposten für Metadaten
   * @returns {Promise<Object>} - Checkout-URL und Session-ID
   */
  async createTemporaryStripeCheckoutSession(amount, items = []) {
    console.log(`[paymentService] Erstelle temporäre Stripe-Session für ${amount}€`);
    
    // Erstellen einer Metadaten-Struktur für Stripe
    const lineItems = items.map(item => ({
      name: item.name || 'Produkt',
      quantity: item.quantity || 1,
      amount: Math.round((item.price || 0) * 100),
      currency: 'eur'
    }));

    try {
      const response = await axios.post(`${API_URL}/payments/create-temporary-session`, {
        amount: amount,
        currency: 'eur',
        skipEmailCollection: true,
        skipAddressCollection: true,
        paymentMethod: 'stripe',
        lineItems: lineItems,
        successUrl: this.getSuccessUrl()
      }, this.axiosConfig);
      
      console.log(`[paymentService] Temporäre Stripe-Session erfolgreich erstellt, Session-ID: ${response.data?.sessionId || 'nicht verfügbar'}`);
      return response;
    } catch (error) {
      console.error("[paymentService] Fehler beim Erstellen der temporären Stripe-Session:", error);
      
      let errorMessage = "Unbekannter Fehler";
      
      // Detaillierte Fehlerdiagnose
      if (error.response) {
        // Der Server hat geantwortet, aber mit einem Fehlercode
        errorMessage = `Serverfehler ${error.response.status}: ${JSON.stringify(error.response.data || {})}`;
        console.error(`[paymentService] Server antwortet mit Status ${error.response.status}:`, error.response.data);
      } else if (error.request) {
        // Der Server hat nicht geantwortet - Netzwerkfehler
        errorMessage = `Verbindungsfehler: Konnte keine Antwort vom Server erhalten. Bitte überprüfen Sie Ihre Internetverbindung. (${error.message})`;
        console.error("[paymentService] Keine Antwort vom Server erhalten - Netzwerkfehler");
      } else {
        console.error("[paymentService] Fehler vor dem Senden der Anfrage:", error.message);
      }
      
      // Gib den Fehler weiter
      throw new Error(`Fehler bei der Stripe-Session: ${errorMessage}`);
    }
  },
  
  /**
   * Stripe-Konfiguration abrufen
   * @returns {Promise<Object>} - Stripe-Konfiguration mit PublishableKey
   */
  getStripeConfig() {
    return axios.get(`${API_URL}/stripe/config`);
  },

  /**
   * Alternative Methode zum Aufrufen des alten Endpunkts (falls nötig)
   * @param {number} orderId - ID der Bestellung
   * @returns {Promise<Object>} - Checkout-URL
   */
  async createLegacyStripeCheckoutSession(orderId) {
    console.log(`[paymentService] Verwende Legacy-Endpunkt für Stripe-Checkout für Bestellung #${orderId}`);
    
    try {
      const response = await axios.post(`${API_URL}/payments/${orderId}/stripe-checkout`, {
        skipEmailCollection: true,
        skipAddressCollection: true
      }, this.axiosConfig);
      
      console.log(`[paymentService] Legacy Stripe-Session erfolgreich erstellt für Bestellung #${orderId}`);
      return response;
    } catch (error) {
      console.error(`[paymentService] Auch Legacy-Endpunkt fehlgeschlagen für Bestellung #${orderId}:`, error);
      
      // Keine weiteren Fallbacks - Fehler weitergeben
      throw error;
    }
  },

  /**
   * Öffnet direkt ein Stripe-Checkout-Fenster
   * @param {number} orderId - ID der Bestellung
   * @returns {Promise<void>} - Promise, das aufgelöst wird, wenn das Fenster geöffnet wurde
   */
  async redirectToStripeCheckout(orderId) {
    try {
      console.log(`[paymentService] Starte Umleitung zum Stripe-Checkout für Bestellung #${orderId}`);
      const response = await this.createStripeCheckoutSession(orderId);
      const { checkoutUrl } = response.data;
      
      // Umleitung zur Stripe-Checkout-Seite
      if (checkoutUrl) {
        console.log(`[paymentService] Leite um zu: ${checkoutUrl}`);
        window.location.href = checkoutUrl;
      } else {
        throw new Error("Keine Checkout-URL in der Antwort gefunden");
      }
    } catch (error) {
      console.error(`[paymentService] Fehler bei der Umleitung zum Stripe-Checkout für Bestellung #${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Bestellung als Barzahlung markieren
   * @param {number} orderId - ID der Bestellung
   * @returns {Promise<Object>} - Ergebnis
   */
  async markAsCashPayment(orderId) {
    console.log(`[paymentService] Markiere Bestellung #${orderId} als Barzahlung`);
    
    try {
      const response = await axios.post(`${API_URL}/payments/${orderId}/mark-as-cash`);
      console.log(`[paymentService] Bestellung #${orderId} erfolgreich als Barzahlung markiert`);
      return response;
    } catch (error) {
      console.error(`[paymentService] Fehler beim Markieren als Barzahlung für Bestellung #${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Bestellung bestätigen (nach erfolgreicher Zahlung)
   * @param {string} paymentIntentId - ID der Zahlung
   * @returns {Promise<Object>} - Ergebnis
   */
  async confirmPayment(paymentIntentId) {
    console.log(`[paymentService] Bestätige Zahlung mit PaymentIntent-ID: ${paymentIntentId}`);
    
    try {
      const response = await axios.post(`${API_URL}/payments/confirm`, { paymentIntentId });
      console.log(`[paymentService] Zahlung erfolgreich bestätigt für PaymentIntent-ID: ${paymentIntentId}`);
      return response;
    } catch (error) {
      console.error(`[paymentService] Fehler bei der Zahlungsbestätigung für PaymentIntent-ID: ${paymentIntentId}:`, error);
      throw error;
    }
  },

  /**
   * Bestellung nach erfolgreicher Zahlung erstellen
   * @param {Object} orderData - Die Bestelldaten
   * @param {string} sessionId - Die Payment Session ID
   * @returns {Promise<Object>} - Die erstellte Bestellung
   */
  async createOrderAfterPayment(orderData, sessionId) {
    console.log(`[paymentService] Erstelle Bestellung nach Zahlung mit Session-ID: ${sessionId}`);
    
    try {
      // 1. Bestellung erstellen
      console.log(`[paymentService] Sende Bestelldaten an API:`, orderData);
      const orderResponse = await axios.post(`${API_URL}/orders`, orderData);
      const orderId = orderResponse.data.id;
      
      console.log(`[paymentService] Bestellung #${orderId} erfolgreich erstellt, verifiziere Zahlung`);
      
      // 2. Zahlungsstatus aktualisieren
      await this.verifyPayment(orderId, sessionId);
      
      console.log(`[paymentService] Zahlung für Bestellung #${orderId} erfolgreich verifiziert`);
      return orderResponse.data;
    } catch (error) {
      console.error("[paymentService] Fehler beim Erstellen der Bestellung nach Zahlung:", error);
      
      // Detaillierte Fehlerausgabe
      if (error.response) {
        console.error(`[paymentService] Server antwortet mit Status ${error.response.status}:`, error.response.data);
      }
      
      throw error;
    }
  },

  /**
   * Zahlungsstatus abrufen
   * @param {number} orderId - ID der Bestellung
   * @returns {Promise<Object>} - Zahlungsstatus
   */
  async getPaymentStatus(orderId) {
    console.log(`[paymentService] Rufe Zahlungsstatus für Bestellung #${orderId} ab`);
    
    try {
      const response = await axios.get(`${API_URL}/payments/status/${orderId}`);
      console.log(`[paymentService] Zahlungsstatus für Bestellung #${orderId}:`, response.data);
      return response;
    } catch (error) {
      console.error(`[paymentService] Fehler beim Abrufen des Zahlungsstatus für Bestellung #${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Zahlungsstatus überprüfen
   * @param {number} orderId - ID der Bestellung
   * @param {string} sessionId - Stripe Session ID
   * @returns {Promise<Object>} - Zahlungsstatus
   */
  async verifyPayment(orderId, sessionId) {
    console.log(`[paymentService] Verifiziere Zahlung für Bestellung #${orderId} mit Session-ID: ${sessionId}`);
    
    try {
      // Maximal 3 Wiederholungsversuche bei Netzwerk- oder Serverfehlern
      let attempts = 0;
      const maxAttempts = 3;
      
      while (attempts < maxAttempts) {
        try {
          const response = await axios.post(`${API_URL}/payments/${orderId}/verify-payment`, JSON.stringify(sessionId), {
            headers: {
              'Content-Type': 'application/json'
            }
          });
          
          console.log(`[paymentService] Zahlung für Bestellung #${orderId} erfolgreich verifiziert:`, response.data);
          return response;
        } catch (error) {
          attempts++;
          
          // Bei bestimmten Fehlern erneut versuchen
          if (error.response && (error.response.status === 502 || error.response.status === 503 || error.response.status === 504)) {
            console.warn(`[paymentService] Serverprobleme beim Verifizieren der Zahlung, Versuch ${attempts}/${maxAttempts}`);
            
            // Kurz warten vor dem nächsten Versuch (exponentielles Backoff)
            if (attempts < maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempts)));
              continue;
            }
          }
          
          // Bei anderen Fehlern oder nach allen Versuchen, den Fehler werfen
          throw error;
        }
      }
    } catch (error) {
      console.error(`[paymentService] Fehler bei der Zahlungsverifikation für Bestellung #${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Zahlungsstatus einer temporären Session überprüfen
   * @param {string} sessionId - Stripe Session ID
   * @returns {Promise<Object>} - Zahlungsstatus
   */
  async verifyTemporaryPayment(sessionId) {
    console.log(`[paymentService] Verifiziere temporäre Zahlung mit Session-ID: ${sessionId}`);
    
    try {
      const response = await axios.post(`${API_URL}/payments/verify-temporary-payment`, JSON.stringify(sessionId), {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log(`[paymentService] Temporäre Zahlung mit Session-ID ${sessionId} erfolgreich verifiziert:`, response.data);
      return response;
    } catch (error) {
      console.error(`[paymentService] Fehler bei der Verifikation der temporären Zahlung mit Session-ID ${sessionId}:`, error);
      throw error;
    }
  },

  /**
   * Bestellung als bezahlt markieren (für Admin)
   * @param {number} orderId - ID der Bestellung
   * @returns {Promise<Object>} - Ergebnis
   */
  async markAsPaid(orderId) {
    console.log(`[paymentService] Markiere Bestellung #${orderId} als bezahlt (Admin)`);
    
    try {
      const response = await axios.post(`${API_URL}/payments/${orderId}/mark-as-paid`);
      console.log(`[paymentService] Bestellung #${orderId} erfolgreich als bezahlt markiert`);
      return response;
    } catch (error) {
      console.error(`[paymentService] Fehler beim Markieren der Bestellung #${orderId} als bezahlt:`, error);
      throw error;
    }
  }
} 